/**
 * 外部ツール（Claude Code 等）から Photo Prep を完全自動運用するための
 * `window.__hitopAuto` API。
 *
 * - UI / UX は完全に不変
 * - 既存の Zustand store + usePipeline フックを再利用
 * - 詳細手順は /process-vehicle スキル参照
 *
 * 注意点：
 * - 本番（HTTPS）から HTTP localhost へ fetch する場合、Mixed Content
 *   ブロックされる可能性あり。回避するには:
 *     a) `npm run dev` でローカル開発サーバー（http://localhost:5173）を使う
 *     b) 公開された HTTPS URL（Cloudflare Tunnel 等）でファイルを配信する
 *     c) `loadFilesFromBlobs()` で File 配列を直接受け取る（fetch しない）
 */
import type { MetadataInput } from '../stores/useAppStore';

export interface HitopAutoApi {
  /** 状態: idle / validating / processing / done / error */
  readonly getPhase: () => string;
  /** 起動準備完了か（files >0 + metadata 妥当） */
  readonly canStart: () => boolean;
  /** 完了 ZIP の Blob と要約 */
  readonly getResult: () => HitopAutoResult | null;
  /** ファイル URL 配列を受けて fetch → File[] → addFiles */
  readonly loadFilesFromUrls: (urls: readonly string[]) => Promise<number>;
  /** File / Blob 配列を直接受けて addFiles */
  readonly loadFilesFromBlobs: (
    items: readonly { name: string; blob: Blob; type?: string }[],
  ) => Promise<number>;
  /** 車両情報を一括設定（部分更新可） */
  readonly setMetadata: (patch: Partial<MetadataInput>) => void;
  /** ヒーロー写真のインデックスを変更 */
  readonly setHeroIndex: (index: number) => void;
  /** 全ファイルクリア */
  readonly clearFiles: () => void;
  /** 処理開始（usePipeline.start を呼ぶ） */
  readonly start: () => Promise<void>;
  /** 完了/エラーから idle に戻す */
  readonly reset: () => void;
  /** done になるまで poll で待つ（最大 timeoutMs） */
  readonly waitForDone: (timeoutMs?: number) => Promise<HitopAutoResult>;
  /**
   * 動画スライド（カーセンサー物件動画用 4:3 MP4）を生成。
   * `start()` 完了後（phase=done）に呼べる。最大 5分程度かかる。
   * 戻り値: 生成された Blob と推奨ファイル名。
   */
  readonly generateVideo: (
    onProgress?: (ratio: number, message: string) => void,
  ) => Promise<HitopVideoResult>;
  /** API バージョン */
  readonly version: string;
}

export interface HitopVideoResult {
  readonly blob: Blob;
  readonly name: string;
  readonly sizeBytes: number;
  readonly durationSec: number;
}

export interface HitopAutoResult {
  readonly phase: 'done' | 'error';
  readonly zipBlob?: Blob;
  readonly zipName?: string;
  readonly summary?: {
    readonly totalPhotos: number;
    readonly heroFallbackUsed: boolean;
    readonly cleanedCount: number;
    readonly elapsedMs: number;
  };
  readonly errorMessage?: string;
}

declare global {
  interface Window {
    __hitopAuto?: HitopAutoApi;
  }
}

export const HITOP_AUTO_API_VERSION = '1.0.0';
